import { useState } from "react";
import { useSite } from "../context/SiteContext";
import { openWA, KISS } from "../lib/wa";
import { trackWa } from "../lib/analytics";

const initial = {
  name: "",
  date: "",
  time: "",
  duration: "1 hour",
  location: "Incall Liverpool",
  extras: [],
  notes: "",
  referral: "",
};

// Which durations to expose as quick-book buttons (order matters).
const QUICK_KEYS = ["1 hour", "90 minutes", "Overnight (8hr)"];

function pickRates(rates) {
  const map = {};
  (rates || []).forEach((r) => (map[r.duration] = r));
  return QUICK_KEYS.map((k) => map[k]).filter(Boolean);
}

function shortLabel(duration) {
  if (/overnight/i.test(duration)) return "overnight";
  if (/^\d+\s*hour/i.test(duration)) return duration.replace(/hour(s)?/i, "hr");
  if (/minute/i.test(duration)) return duration.replace(/minutes?/i, "min");
  return duration;
}

export default function Booking() {
  const { content } = useSite();
  const rates = content.rates || [];
  const extras = content.extras || [];
  const quickRates = pickRates(rates);
  const [form, setForm] = useState({
    ...initial,
    duration: rates[1]?.duration || "1 hour",
  });

  const toggleExtra = (name) =>
    setForm((f) => ({
      ...f,
      extras: f.extras.includes(name)
        ? f.extras.filter((x) => x !== name)
        : [...f.extras, name],
    }));

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const parts = [
      `Hi Camilla ${KISS} — I'd like to book.`,
      form.name && `Name: ${form.name}.`,
      form.date && `Date: ${form.date}.`,
      form.time && `Time: ${form.time}.`,
      `Duration: ${form.duration}.`,
      `Location: ${form.location}.`,
      form.extras.length && `Extras: ${form.extras.join(", ")}.`,
      form.referral && `Referral code: ${form.referral.trim().toUpperCase()}.`,
      form.notes && `Notes: ${form.notes}.`,
    ]
      .filter(Boolean)
      .join(" ");
    trackWa("booking_form_submit");
    openWA(parts);
  };

  const quickBook = (r) => {
    const key = /overnight/i.test(r.duration)
      ? "quick_book_overnight"
      : /90/.test(r.duration)
      ? "quick_book_90min"
      : "quick_book_1hr";
    trackWa(key);
    openWA(
      `Hi Camilla ${KISS} — I'd like to book a ${r.duration} session (${r.incall} incall). When are you available?`
    );
  };

  return (
    <section id="booking" className="section" data-testid="booking-section">
      <div className="container-x px-5 md:px-8">
        {/* Quick-book row */}
        {quickRates.length > 0 && (
          <div className="mb-14" data-testid="quick-book-row">
            <div className="text-center max-w-xl mx-auto mb-6">
              <div className="eyebrow justify-center mx-auto mb-3">Quick book</div>
              <p className="font-serif italic text-2xl md:text-3xl text-gold-2">
                In a rush? Tap and I'll be waiting 💋
              </p>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 md:gap-4">
              {quickRates.map((r) => (
                <button
                  key={r.duration}
                  onClick={() => quickBook(r)}
                  className="btn-gold"
                  data-testid={`quick-book-${shortLabel(r.duration).replace(/\s+/g, "-").toLowerCase()}`}
                >
                  Book {shortLabel(r.duration)} — {r.incall}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-5">Book</div>
            <h2 className="font-display text-4xl md:text-6xl text-gold leading-tight mb-4">
              Or fill in the details below.
            </h2>
            <p className="text-ivory-dim text-base md:text-lg leading-relaxed mb-6">
              I'll open WhatsApp with your message pre-filled. Or just message
              me directly — I don't bite (unless you ask).
            </p>
            <button
              className="btn-gold"
              onClick={() =>
                openWA(
                  `Hi Camilla ${KISS} — just saying hi. Can you tell me your availability this week?`
                )
              }
              data-testid="booking-direct-wa-btn"
            >
              Or just message me on WhatsApp →
            </button>

            <div className="hairline my-10 max-w-xs" />
            <p className="text-xs md:text-sm text-[color:var(--muted)] leading-relaxed">
              This form does not submit or store data anywhere. It only opens
              WhatsApp with your details typed for you.
            </p>
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-7 card-luxe p-6 md:p-8 space-y-5"
            data-testid="booking-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                  Name
                </span>
                <input
                  className="input-luxe mt-2"
                  placeholder="First name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  required
                  data-testid="booking-name-input"
                />
              </label>
              <label className="block">
                <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                  Preferred date
                </span>
                <input
                  type="date"
                  className="input-luxe mt-2"
                  value={form.date}
                  onChange={(e) => update("date", e.target.value)}
                  data-testid="booking-date-input"
                />
              </label>
              <label className="block">
                <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                  Preferred time
                </span>
                <input
                  type="time"
                  className="input-luxe mt-2"
                  value={form.time}
                  onChange={(e) => update("time", e.target.value)}
                  data-testid="booking-time-input"
                />
              </label>
              <label className="block">
                <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                  Duration
                </span>
                <select
                  className="input-luxe mt-2"
                  value={form.duration}
                  onChange={(e) => update("duration", e.target.value)}
                  data-testid="booking-duration-select"
                >
                  {rates.map((r) => (
                    <option key={r.duration} value={r.duration}>
                      {r.duration}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block sm:col-span-2">
                <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                  Service type
                </span>
                <select
                  className="input-luxe mt-2"
                  value={form.location}
                  onChange={(e) => update("location", e.target.value)}
                  data-testid="booking-location-select"
                >
                  <option>Incall Liverpool</option>
                  <option>Outcall Sydney metro</option>
                </select>
              </label>
            </div>

            <fieldset>
              <legend className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--muted)] mb-3">
                Extras (optional)
              </legend>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 max-h-56 overflow-auto pr-2">
                {extras.map((e) => (
                  <label
                    key={e.name}
                    className="flex items-center gap-2 text-sm text-ivory-dim cursor-pointer hover:text-ivory transition-colors"
                    data-testid={`booking-extra-${e.name.split(/[^a-z0-9]+/i)[0].toLowerCase()}`}
                  >
                    <input
                      type="checkbox"
                      className="accent-[color:var(--gold)]"
                      checked={form.extras.includes(e.name)}
                      onChange={() => toggleExtra(e.name)}
                    />
                    <span>
                      {e.name}{" "}
                      <span className="text-gold text-xs">{e.price}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="block">
              <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                Notes / fantasies
              </span>
              <textarea
                rows={4}
                className="input-luxe mt-2 resize-y"
                placeholder="First time, be gentle — tell me anything you'd like me to know."
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                data-testid="booking-notes-input"
              />
            </label>

            <label className="block">
              <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                Referral / promo code (optional)
              </span>
              <input
                className="input-luxe mt-2 uppercase"
                placeholder="CAM-XXXXXX"
                value={form.referral}
                onChange={(e) => update("referral", e.target.value)}
                data-testid="booking-referral-input"
              />
              <div className="text-[11px] text-[color:var(--muted)] mt-1">
                Got a referral code from a friend? Enter it above — $50 off your booking 💋
              </div>
            </label>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                className="btn-wine"
                data-testid="booking-submit-btn"
              >
                Send via WhatsApp →
              </button>
              <button
                type="button"
                className="btn-ghost-gold"
                onClick={() =>
                  setForm({ ...initial, duration: rates[1]?.duration || "1 hour" })
                }
                data-testid="booking-reset-btn"
              >
                Reset
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
